<template>
  <section
    class="flex w-full flex-col items-center justify-center gap-4 px-4 py-16 md:px-8 rounded-md"
  >
    <div class="max-w-lg w-full mx-auto flex items-start">
      <div class="flex flex-initial">
        <BaseButton color="light" size="extra-small" @click="handleBack">
          <div class="gap-2 flex items-center">
            <BaseIcon icon="arrow-left" />
            <span>Back</span>
          </div>
        </BaseButton>
      </div>
    </div>

    <div
      class="bg-white w-full max-w-lg rounded-3xl border-shadow p-6 md:p-8 md:px-12"
    >
      <div class="mb-8 flex flex-col items-start gap-4">
        <div class="flex flex-col gap-2 md:gap-4">
          <div>
            <a
              title="Go to Capino homepage"
              class="inline-flex flex-initial cursor-pointer duration-200 hover:opacity-80"
              href="/"
            >
              <BaseLogo :is-label="false" />
            </a>
          </div>

          <h1 class="text-xl font-bold text-gray-900 md:text-2xl">
            Welcome to Capino
          </h1>
        </div>

        <div class="text-gray-600 leading-tight">
          Create a free account to access Capino's smart grammar training.
        </div>
      </div>

      <BaseButton
        class="w-full"
        :is-loading="isLoadingWithGoogle"
        :is-disabled="isLoadingWithEmail"
        @click="handleSubmitWithGoogle"
      >
        <div class="flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            viewBox="0 0 48 48"
          >
            <path
              fill="#FFC107"
              d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
            ></path>
            <path
              fill="#FF3D00"
              d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
            ></path>
            <path
              fill="#4CAF50"
              d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
            ></path>
            <path
              fill="#1976D2"
              d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
            ></path>
          </svg>
          <span>Sign up with Google</span>
        </div>
      </BaseButton>

      <div
        class="flex items-center justify-center gap-8 py-8 text-sm text-gray-600/50"
      >
        <div class="w-full h-px bg-[#E8E8E8]"></div>
        <span class="tracking-[1px]">or</span>
        <div class="w-full h-px bg-[#E8E8E8]"></div>
      </div>

      <BaseForm ref="form">
        <BaseFormField
          :value="form.email"
          :rules="[$rules.required, $rules.email, v => $rules.counter(v, 50)]"
        >
          <template #default="{ errors }">
            <BaseInput
              v-model="form.email"
              type="email"
              label="Email"
              placeholder="hello@gmail.com"
              :errors="errors"
            />
          </template>
        </BaseFormField>
      </BaseForm>

      <BaseButton
        class="w-full mt-4"
        color="primary-gradient"
        :is-loading="isLoadingWithEmail"
        :is-disabled="isLoadingWithGoogle"
        @click="handleSubmitWithEmail"
      >
        Sign up with email
      </BaseButton>
    </div>

    <p class="text-gray-500 text-center text-sm opacity-75">
      By signing up, you agree to our
      <router-link
        target="_blank"
        class="hover:underline"
        :to="{ name: 'public.terms-of-services' }"
        >Terms of Service</router-link
      >.
    </p>
  </section>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import BaseLogo from '@/components/Base/BaseLogo.vue'
import BaseButton from '@/components/Base/BaseButton.vue'

import BaseInput from '@/components/Base/BaseInput.vue'
import BaseForm from '@/components/Base/BaseForm/BaseForm.vue'
import BaseFormField from '@/components/Base/BaseForm/BaseFormField.vue'

import * as services from '@/services'
import { isEmbeddedBrowser } from '@/utils/base'
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth'

export default {
  name: 'SigninFormCard',

  components: {
    BaseLogo,
    BaseButton,

    BaseInput,
    BaseForm,
    BaseFormField
  },

  data: () => ({
    form: {
      email: ''
    },
    isLoadingWithEmail: false,
    isLoadingWithGoogle: false
  }),

  computed: {
    ...mapGetters('locale', ['currentLocale'])
  },

  methods: {
    ...mapActions('auth', {
      login: 'LOGIN'
    }),

    async handleSubmitWithEmail() {
      window?.datafast('signin-started')

      const isValid = this.defineIsValid()

      if (!isValid) return

      try {
        this.isLoadingWithEmail = true

        const { isSuccess } = await services.auth.generateOtp({
          email: this.form.email,
          locale: this.currentLocale
          // plan: this.$route.params.plan
        })

        if (!isSuccess) return

        this.$emit('open-verify-card', { email: this.form.email })
      } finally {
        this.isLoadingWithEmail = false
      }
    },

    async handleSubmitWithGoogle() {
      window?.datafast('signin-started')

      if (isEmbeddedBrowser()) {
        window.alert(this.$t('auth.googleEmbeddedBrowserAlert'))
        return
      }

      try {
        this.isLoadingWithGoogle = true

        const googleProvider = new GoogleAuthProvider({
          prompt: 'select_account'
        })

        const result = await signInWithPopup(
          this.$firebase.auth,
          googleProvider
        )

        const idToken = await result.user.getIdToken()

        const payload = {
          idToken,
          email: result.user.email,
          name: result.user.displayName,
          photoURL: result.user.photoURL,
          providerId: result.providerId
        }

        const { isSuccess, content, error } = await this.login({
          payload,
          type: 'social'
        })

        this.$router.push({ name: 'signin.success' })

        if (!isSuccess) return
      } catch (error) {
        console.log('Google Sign-in Error:', error)
      } finally {
        this.isLoadingWithGoogle = false
      }
    },

    handleBack() {
      this.$router.push({ name: 'home' })
    },

    defineIsValid() {
      return this.$refs.form.defineIsValid()
    }
  }
}
</script>
